import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLazyViewSolarEstimationTimelineQuery } from "../../../../../redux/api/solar-estimation/solarEstimationAPI";
import ViewSolarEstimationTimelineResponseDTO from "../../../../../shared/oversight-core/dtos/response-dtos/view-solar-estimation-timeline-response-dto";
import { EstimationStatus } from "../../../../../shared/oversight-core/enums/estimation-status";
import MaterialIcon from "../../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import SpinnerModal from "../../../../../shared/oversight-core/ui-elements/spinner/spinner";
import {
  convertUTCToLocal,
  getRelativeTimeFromMinutes,
} from "../../../../../shared/oversight-core/utils/time-utils";
import EstimationSummaryExportModal from "../estimation-summary-export-modal/estimation-summary-export-modal";

interface IProps extends ModelContainerProps {
  show: boolean;
  estimationId: string;
}
const EstimationTimelineModal = (props: IProps) => {
  const { show, estimationId, ...rest } = props;

  const [estimationTimeline, setEstimationTimeline] =
    useState<ViewSolarEstimationTimelineResponseDTO>();
  const [clientEmail, setClientEmail] = useState<string>("");
  const [clientMobileNumber, setClientMobileNumber] = useState<string>("");
  const [
    showEstimationSummaryExportModal,
    setShowEstimationSummaryExportModal,
  ] = useState(false);

  const [triggerSolarEstimationTimeline, { isFetching }] =
    useLazyViewSolarEstimationTimelineQuery();

  useEffect(() => {
    if (show && estimationId) {
      triggerSolarEstimationTimeline({ estimationId })
        .unwrap()
        .then((res: ViewSolarEstimationTimelineResponseDTO) => {
          setEstimationTimeline(res);
          setClientEmail(res.timeline?.[0]?.user.email || "");
          setClientMobileNumber(res.timeline?.[0]?.user.contactNumber || "");
        })
        .catch(() => {
          setClientEmail("");
        });
    }
  }, [show, estimationId]);

  const getStatusName = (status: EstimationStatus) => {
    switch (status) {
      case EstimationStatus.APPROVED:
        return "Approved";
      case EstimationStatus.APPROVED_WITH_CHANGES:
        return "Approved with Changes";
      case EstimationStatus.MODIFIED:
        return "Modified";
      case EstimationStatus.SUBMITTED:
        return "Submitted";
      case EstimationStatus.REJECTED:
        return "Rejected";
      case EstimationStatus.PURCHASED:
        return "Purchased";
      default:
        return "";
    }
  };

  const getStatusColor = (status: EstimationStatus) => {
    switch (status) {
      case EstimationStatus.APPROVED:
        return "text-color-11 bg-background-19";
      case EstimationStatus.APPROVED_WITH_CHANGES:
        return "text-color-11 bg-background-19";
      case EstimationStatus.MODIFIED:
        return "text-color-12 bg-background-20";
      case EstimationStatus.SUBMITTED:
        return "text-color-12 bg-background-20";
      case EstimationStatus.REJECTED:
        return "text-danger bg-background-21";
      case EstimationStatus.PURCHASED:
        return "status-approved bg-background-22";
      default:
        return "";
    }
  };

  const handleClickEmailClient = (email: string) => {
    if (!email) return;

    const domain = email.split("@")[1]?.toLowerCase();

    if (domain === "gmail.com") {
      globalThis.open(
        `https://mail.google.com/mail/?view=cm&tab=1&to=${email}`,
        "_blank"
      );
    } else {
      globalThis.location.href = `mailto:${email}`;
    }
  };

  const getTimeColors = (minutes: number) => {
    if (minutes < 10080) {
      return "bg-background-17 text-color-9";
    } else {
      return "bg-background-18 text-color-10";
    }
  };

  return (
    <ModalContainer
      {...rest}
      title={
        <>
          <Row>
            <Col className="modal-title">Estimation Timeline</Col>
          </Row>
          <Row>
            <Col className="text-color-2 font-size-14 font-weight-400">
              #{estimationTimeline?.estimationNumber} |{" "}
              {estimationTimeline?.title}
            </Col>
          </Row>
        </>
      }
      show={show}
      size="modal-lg"
      isButtonsVisible={false}
    >
      <div>
        {estimationTimeline?.timeline &&
          estimationTimeline?.timeline.length > 0 &&
          estimationTimeline?.timeline.map((item, index) => {
            return (
              <Row
                key={item.user.id + index}
                className="row-cols-auto align-items-center mb-2"
              >
                <Col className="text-color-2 font-weight-400 font-size-12 w-25 pe-0">
                  {convertUTCToLocal(item.date)}
                </Col>
                {item?.durationMinutes ? (
                  <Col
                    className={`${getTimeColors(
                      item?.durationMinutes
                    )} font-weight-400 font-size-12 rounded-4 py-1 px-3 me-2`}
                  >
                    {getRelativeTimeFromMinutes(item?.durationMinutes)}
                  </Col>
                ) : (
                  ""
                )}
                <Col
                  className={`font-weight-400 font-size-12 rounded-4 py-1 px-2 ${getStatusColor(
                    item?.estimationStatus
                  )}`}
                >
                  {getStatusName(item?.estimationStatus)}&nbsp;by&nbsp;
                  {item.user.firstName}&nbsp;{item.user.lastName}
                </Col>
              </Row>
            );
          })}
        <Row className="mt-4 pt-3 mx-0">
          <Col
            className={`col-auto font-weight-400 font-size-16 rounded-4 py-1 px-3 ${getTimeColors(
              estimationTimeline?.totalDurationInMinutes
                ? estimationTimeline?.totalDurationInMinutes
                : 0
            )}`}
          >
            Total Time :&nbsp;
            {getRelativeTimeFromMinutes(
              estimationTimeline?.totalDurationInMinutes
                ? estimationTimeline?.totalDurationInMinutes
                : 0
            )}
          </Col>
        </Row>
        <Row className="row-cols-auto mt-4 pt-3 g-3">
          <Col>
            <a
              href={`tel:${clientMobileNumber}`}
              className="bg-background-16 text-decoration-none border border-2 rounded-2 d-flex align-items-center justify-content-center py-2 px-3 cursor-pointer"
            >
              <Row className="align-items-center">
                <Col className="col-auto pe-0">
                  <MaterialIcon
                    size={18}
                    icon="call"
                    color="#384F82"
                    weight={600}
                  />
                </Col>
                <Col className="text-primary font-weight-600 font-size-14">
                  Call Client
                </Col>
              </Row>
            </a>
          </Col>
          <Col>
            <button
              onClick={() => {
                handleClickEmailClient(clientEmail);
              }}
              className="bg-primary border border-2 border-primary rounded-2 d-flex align-items-center justify-content-center py-2 px-3 cursor-pointer"
            >
              <Row className="align-items-center">
                <Col className="col-auto pe-0">
                  <MaterialIcon
                    size={18}
                    icon="mail"
                    color="#FFFFFF"
                    weight={600}
                  />
                </Col>
                <Col className="text-white font-weight-600 font-size-14">
                  Email Client
                </Col>
              </Row>
            </button>
          </Col>
          <Col>
            <button
              className="bg-primary border border-2 border-primary rounded-2 d-flex align-items-center justify-content-center py-2 px-3 cursor-pointer"
              onClick={() => setShowEstimationSummaryExportModal(true)}
            >
              <Row className="align-items-center">
                <Col className="col-auto pe-0">
                  <MaterialIcon
                    size={18}
                    icon="upload"
                    color="#FFFFFF"
                    weight={600}
                  />
                </Col>
                <Col className="text-white font-weight-600 font-size-14">
                  Export
                </Col>
              </Row>
            </button>
          </Col>
        </Row>
        <EstimationSummaryExportModal
          show={showEstimationSummaryExportModal}
          onCancel={() => setShowEstimationSummaryExportModal(false)}
          onClose={() => setShowEstimationSummaryExportModal(false)}
          estimationId={estimationId}
          isTimeline={true}
        />
        <SpinnerModal show={isFetching} />
      </div>
    </ModalContainer>
  );
};

export default EstimationTimelineModal;
